import { Checkbox } from '@chakra-ui/react';
import { Dispatch, SetStateAction } from 'react';
import {
  AggregatedMetricsTableData,
  isFullMetricQuarterlyLevel,
  MetricsTableData,
  TagOptions,
} from '../../..';
import { MetricCheckboxSelector } from '../CheckboxSelector/CheckboxSelector';
import { TableData } from 'Molecules/NestedTable';
import { QuestionType_Enum_ } from 'models';
import { AssessableMetrics, isSpecialRow } from '../../../Metrics';
import { Row } from '@tanstack/react-table';
import { uniq } from 'lodash';
import { FrequencyEnums, QUARTERS_ARRAY, TimePeriods, TimePeriodsEnums } from 'containers/Esrs';

export const AISelectedRows = ({
  isNarrative,
  row,
  selectedNarrativeMetrics,
  setSelectedNarrativeMetrics,
  selectedNumericMetrics,
  setSelectedNumericMetrics,
  checkboxesDisabled,
}: {
  isNarrative: boolean;
  row: TableData<AggregatedMetricsTableData>;
  selectedNumericMetrics?: MetricsTableData[];
  setSelectedNumericMetrics?: Dispatch<SetStateAction<MetricsTableData[]>>;
  selectedNarrativeMetrics?: AssessableMetrics;
  setSelectedNarrativeMetrics?: Dispatch<SetStateAction<AssessableMetrics>>;
  checkboxesDisabled?: boolean;
}) => {
  if (isNarrative) {
    const isParent =
      row.metric.childrenMetrics?.length > 0 && row.metric.metricType !== QuestionType_Enum_.YesNo_;

    const narrativeChildren = row.metric.childrenMetrics
      ?.map((c) => c.childMetric)
      .filter((m) => m?.metricType !== QuestionType_Enum_.Decimal_) as AssessableMetrics;

    const numericChildren = row.metric.childrenMetrics
      ?.map((c) => c.childMetric)
      .filter((m) => m && m.metricType === QuestionType_Enum_.Decimal_)
      .map((m) => m && { metric: m }) as MetricsTableData[];

    const isChecked = isParent
      ? narrativeChildren.every((c) =>
          selectedNarrativeMetrics?.some((r) => r.reference === c.reference)
        ) &&
        numericChildren.every((c) =>
          selectedNumericMetrics?.some((r) => r.metric.reference === c.metric.reference)
        )
      : row.metric.metricType !== QuestionType_Enum_.Decimal_
        ? (selectedNarrativeMetrics?.some((met) => met.reference === row.metric.reference) ?? false)
        : (selectedNumericMetrics?.some((met) => met.metric.reference === row.metric.reference) ??
          false);

    const isIntermediate =
      isParent && !isChecked
        ? narrativeChildren.some((c) =>
            selectedNarrativeMetrics?.some((r) => r.reference === c.reference)
          ) ||
          numericChildren.some((c) =>
            selectedNumericMetrics?.some((r) => r.metric.reference === c.metric.reference)
          )
        : false;

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isParent) {
        const childrenMetrics = row.metric.childrenMetrics ?? [];
        const narrativeChildren = childrenMetrics
          .map((c) => c.childMetric)
          .filter((m) => m?.metricType !== QuestionType_Enum_.Decimal_) as AssessableMetrics;
        const numericChildren = childrenMetrics
          .map((c) => c.childMetric)
          .filter((m) => m && m.metricType === QuestionType_Enum_.Decimal_)
          .map((m) => m && { metric: m }) as MetricsTableData[];

        if (e.target.checked) {
          // Add narrative children
          setSelectedNarrativeMetrics?.([
            ...(selectedNarrativeMetrics ?? []),
            ...narrativeChildren,
          ]);
          // Add numeric children
          setSelectedNumericMetrics?.([...(selectedNumericMetrics ?? []), ...numericChildren]);
        } else {
          const narrativeReferences = narrativeChildren.map((m) => m.reference);
          const numericReferences = numericChildren.map((m) => m.metric.reference);

          setSelectedNarrativeMetrics?.(
            (selectedNarrativeMetrics ?? []).filter(
              (r) => !narrativeReferences.includes(r.reference)
            )
          );
          setSelectedNumericMetrics?.(
            (selectedNumericMetrics ?? []).filter(
              (r) => !numericReferences.includes(r.metric.reference)
            )
          );
        }
      } else {
        if (row.metric.metricType !== QuestionType_Enum_.Decimal_) {
          if (e.target.checked) {
            setSelectedNarrativeMetrics?.([
              ...(selectedNarrativeMetrics ?? []),
              row.metric as AssessableMetrics[number],
            ]);
          } else {
            setSelectedNarrativeMetrics?.(
              (selectedNarrativeMetrics ?? []).filter((r) => r.reference !== row.metric.reference)
            );
          }
        } else {
          if (e.target.checked) {
            setSelectedNumericMetrics?.([
              ...(selectedNumericMetrics ?? []),
              { metric: row.metric } as MetricsTableData,
            ]);
          } else {
            setSelectedNumericMetrics?.(
              (selectedNumericMetrics ?? []).filter(
                (r) => r.metric.reference !== row.metric.reference
              )
            );
          }
        }
      }
    };
    return (
      <Checkbox
        isDisabled={checkboxesDisabled}
        isChecked={isChecked}
        isIndeterminate={isIntermediate}
        onChange={handleCheckboxChange}
      />
    );
  }
  return (
    selectedNumericMetrics &&
    setSelectedNumericMetrics && (
      <MetricCheckboxSelector
        row={row}
        selectedRows={selectedNumericMetrics}
        setSelectedRows={setSelectedNumericMetrics}
        isDisabled={checkboxesDisabled ?? false}
      />
    )
  );
};

export const createTagCombination = (tags?: TagOptions) => {
  if (!tags || tags.length === 0) {
    return 'None';
  }

  // Sort tags by tagType and tagValue to ensure consistent comparison
  const sortedTags = tags
    .map((tag) => `${tag.tagType}|${tag.tagValue}`)
    .sort()
    .join(';;');

  return sortedTags;
};

export const filterSelectedSubRows = (
  row: AggregatedMetricsTableData,
  selectedQuarter?: TimePeriods
) => {
  return (
    !isSpecialRow(row) &&
    (selectedQuarter
      ? (!!row.subRows?.length && isFullMetricQuarterlyLevel(row.metric)) ||
        (selectedQuarter === 'Year'
          ? row.metric.materialMetrics?.[0]?.frequency === FrequencyEnums.yearly
          : row.metric.materialMetrics?.[0]?.frequency === FrequencyEnums.quarterly)
      : true)
  );
};

export const mapSubRowsKeys = (
  row: AggregatedMetricsTableData,
  selectedQuarter?: TimePeriods
): SelectedRowData[] => {
  const isTagNameRow = !!row.subRows?.length && row.tagName && row.type === 'tag';
  const includeRow =
    !isSpecialRow(row) &&
    (selectedQuarter
      ? (!!row.subRows?.length &&
          isFullMetricQuarterlyLevel(row.metric) &&
          selectedQuarter !== 'Year') ||
        (selectedQuarter === 'Year'
          ? row.metric.materialMetrics?.[0]?.frequency === FrequencyEnums.yearly &&
            !isFullMetricQuarterlyLevel(row.metric)
          : row.metric.materialMetrics?.[0]?.frequency === FrequencyEnums.quarterly)
      : true);
  const timeframe =
    row.metric.materialMetrics?.[0]?.frequency === FrequencyEnums.yearly
      ? TimePeriodsEnums.year
      : QUARTERS_ARRAY.find((quarter) => quarter === selectedQuarter);
  const key = selectedQuarter
    ? [
        {
          rowKey: `${row.metric.reference}${row.tagCombination}${timeframe}`,
          tagCombination: row.tagCombination,
          metricRef: row.metric.reference,
          timeframe: timeframe ?? '',
          tags: row.tags,
          metricType: row.metric.metricType,
        },
      ]
    : [
        {
          rowKey: `${row.metric.reference}${row.tagCombination}${timeframe}`,
          tagCombination: row.tagCombination,
          metricRef: row.metric.reference,
          timeframe: TimePeriodsEnums.year,
          tags: row.tags,
          metricType: row.metric.metricType,
        },
        ...QUARTERS_ARRAY.map((quarter) => ({
          rowKey: `${row.metric.reference}${row.tagCombination}${timeframe}`,
          tagCombination: row.tagCombination,
          metricRef: row.metric.reference,
          timeframe: quarter,
          tags: row.tags,
          metricType: row.metric.metricType,
        })),
      ];
  if (row.subRows?.length) {
    return isTagNameRow || !includeRow
      ? row.subRows
          .filter((subR) => filterSelectedSubRows(subR, selectedQuarter))
          .flatMap((subR) => mapSubRowsKeys(subR, selectedQuarter))
      : [
          ...key,
          ...row.subRows
            .filter((subR) => filterSelectedSubRows(subR, selectedQuarter))
            .flatMap((subR) => mapSubRowsKeys(subR, selectedQuarter)),
        ];
  }
  return includeRow ? key : [];
};

export type SelectedRowData = {
  rowKey?: string;
  metricRef: string;
  timeframe: string;
  tags: AggregatedMetricsTableData['tags'];
  disclosureRequirementRef?: string;
  tagCombination?: string;
  metricType?: QuestionType_Enum_ | null;
  mdrmRef?: string;
};

export const SelectedRows = ({
  row,
  selectedRows,
  setSelectedRows,
  selectedQuarter,
}: {
  row: Row<TableData<AggregatedMetricsTableData>>;
  selectedQuarter: TimePeriods;
  selectedRows: SelectedRowData[];
  setSelectedRows: Dispatch<SetStateAction<SelectedRowData[]>>;
}) => {
  const timeframe =
    row.original.metric.materialMetrics?.[0]?.frequency === FrequencyEnums.yearly
      ? TimePeriodsEnums.year
      : QUARTERS_ARRAY.find((quarter) => quarter === selectedQuarter);
  const key = {
    rowKey: `${row.original.metric.reference}${row.original.tagCombination}${timeframe}`,
    tagCombination: row.original.tagCombination,
    metricRef: row.original.metric.reference,
    timeframe: timeframe ?? '',
    tags: row.original.tags,
  };
  const subRows = row.subRows.filter((subR) =>
    filterSelectedSubRows(subR.original, selectedQuarter)
  );

  const isTagNameRow = !!row.subRows.length && row.original.tagName && row.original.type === 'tag';

  const checkIfSubrowsSelected = () => {
    const keys = row.subRows.flatMap((sR) => mapSubRowsKeys(sR.original, selectedQuarter));
    return keys.every((subR) => selectedRows.some((sR) => sR.rowKey === subR.rowKey));
  };

  const isSelected = isTagNameRow
    ? checkIfSubrowsSelected()
    : selectedRows?.some((selectedRow) => key.rowKey === selectedRow.rowKey);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      if (row.subRows) {
        const keys = subRows.flatMap((sR) => mapSubRowsKeys(sR.original, selectedQuarter));
        setSelectedRows(
          isTagNameRow ? uniq([...keys, ...selectedRows]) : uniq([...keys, ...selectedRows, key])
        );
      } else {
        setSelectedRows(uniq([...selectedRows, key]));
      }
    } else {
      if (row.subRows) {
        const keys = subRows.flatMap((sR) => mapSubRowsKeys(sR.original, selectedQuarter));
        setSelectedRows(
          selectedRows.filter((selectedRow) =>
            [...keys, key].every((key) => key.rowKey !== selectedRow.rowKey)
          )
        );
      } else {
        setSelectedRows(selectedRows.filter((selectedRow) => key.rowKey !== selectedRow.rowKey));
      }
    }
  };
  return <Checkbox isChecked={isSelected} onChange={handleCheckboxChange} />;
};
